
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/feature/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const privacidadeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Política de Privacidade | Carta do Eu Futuro',
  url: `${SITE_URL}/privacidade`,
  description: 'Política de Privacidade do Carta do Eu Futuro. Saiba como coletamos, usamos e protegemos seus dados pessoais conforme a LGPD.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Privacidade', item: `${SITE_URL}/privacidade` },
    ],
  },
};

const Privacidade = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-luxury-ivory">
      <SEOHead
        title="Política de Privacidade | Carta do Eu Futuro"
        description="Política de Privacidade do Carta do Eu Futuro. Saiba como coletamos, usamos e protegemos seus dados pessoais conforme a LGPD."
        keywords="política de privacidade, LGPD, proteção de dados, carta do eu futuro"
        canonical="/privacidade"
        schema={privacidadeSchema}
      />
      {/* Navbar */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-luxury-black/90 backdrop-blur-md border-b border-gold-500/10">
        <div className="container mx-auto px-6 lg:px-16 flex items-center justify-between h-24">
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <img
              src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
              alt="Carta do Eu Futuro"
              className="w-[72px] h-[72px] object-contain group-hover:scale-110 group-hover:drop-shadow-[0_0_14px_rgba(201,164,64,0.55)] transition-all duration-300"
            />
            <span className="font-['Playfair_Display'] text-lg font-semibold text-white">
              Carta do Eu Futuro
            </span>
          </Link>
          <div className="flex items-center gap-8">
            <Link to="/" className="font-['Montserrat'] text-xs text-white/50 hover:text-gold-400 transition-colors cursor-pointer tracking-wider uppercase">Início</Link>
            <Link to="/dashboard" className="font-['Montserrat'] text-xs text-white/50 hover:text-gold-400 transition-colors cursor-pointer tracking-wider uppercase">Minhas Cartas</Link>
            <Link to="/faq" className="font-['Montserrat'] text-xs text-white/50 hover:text-gold-400 transition-colors cursor-pointer tracking-wider uppercase">FAQ</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-luxury-black via-luxury-charcoal to-luxury-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-96 h-96 bg-gold-500 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold-600 rounded-full blur-[120px]"></div>
        </div>
        <div className="container mx-auto px-6 lg:px-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/10 border border-gold-500/20 mb-6">
              <i className="ri-shield-check-line text-gold-400"></i>
              <span className="font-['Montserrat'] text-xs text-gold-400 tracking-wider uppercase">Sua privacidade é nossa prioridade</span>
            </div>
            <h1 className="font-['Playfair_Display'] text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">Política de Privacidade</h1>
            <p className="font-['Montserrat'] text-sm text-white/60 leading-relaxed tracking-wide max-w-2xl mx-auto">Última atualização: Janeiro de 2025</p>
          </div>
        </div>
      </section>

      {/* Conteúdo */}
      <section className="py-20">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="max-w-4xl mx-auto">
            <div className="mb-16">
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-4">
                A <strong className="text-luxury-charcoal">Carta do Eu Futuro</strong> respeita sua privacidade e está comprometida em proteger seus dados pessoais. Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos suas informações quando você utiliza nosso serviço.
              </p>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">
                Ao utilizar nosso site e serviços, você concorda com as práticas descritas nesta política.
              </p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">1. Informações que Coletamos</h2>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">1.1 Dados Fornecidos por Você</h3>
              <ul className="list-disc list-inside space-y-2 mb-6 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Nome completo</li>
                <li>Endereço de email</li>
                <li>CPF ou CNPJ (para processamento de pagamento)</li>
                <li>WhatsApp (opcional)</li>
                <li>Idade (opcional)</li>
                <li>Conteúdo da sua carta (situação atual, desejos futuros, mensagem pessoal)</li>
                <li>Prazo de entrega escolhido</li>
              </ul>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">1.2 Dados Coletados Automaticamente</h3>
              <ul className="list-disc list-inside space-y-2 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Endereço IP</li>
                <li>Tipo de navegador e dispositivo</li>
                <li>Páginas visitadas e tempo de navegação</li>
                <li>Cookies e tecnologias similares</li>
              </ul>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">2. Como Usamos Suas Informações</h2>
              <ul className="list-disc list-inside space-y-2 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li><strong className="text-luxury-charcoal">Processar sua carta:</strong> Armazenar e entregar sua carta no prazo escolhido</li>
                <li><strong className="text-luxury-charcoal">Processar pagamentos:</strong> Através do Asaas, nosso parceiro de pagamentos</li>
                <li><strong className="text-luxury-charcoal">Enviar notificações:</strong> Confirmação de pagamento, lembretes e entrega da carta</li>
                <li><strong className="text-luxury-charcoal">Melhorar nosso serviço:</strong> Análise de uso e experiência do usuário</li>
                <li><strong className="text-luxury-charcoal">Suporte ao cliente:</strong> Responder dúvidas e resolver problemas</li>
                <li><strong className="text-luxury-charcoal">Cumprir obrigações legais:</strong> Quando exigido por lei</li>
              </ul>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">3. Compartilhamento de Informações</h2>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">3.1 Parceiros de Tecnologia</h3>
              <ul className="list-disc list-inside space-y-2 mb-6 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li><strong className="text-luxury-charcoal">Supabase:</strong> Armazenamento seguro de dados e autenticação</li>
                <li><strong className="text-luxury-charcoal">Asaas:</strong> Processamento de pagamentos (PIX, cartão de crédito, boleto)</li>
                <li><strong className="text-luxury-charcoal">Readdy.ai:</strong> Plataforma de hospedagem do site</li>
              </ul>
              <h3 className="font-['Montserrat'] text-base font-semibold text-luxury-charcoal mb-4 tracking-wide">3.2 Quando Compartilhamos</h3>
              <ul className="list-disc list-inside space-y-2 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Você nos autoriza expressamente</li>
                <li>Exigido por lei ou ordem judicial</li>
                <li>Para proteger nossos direitos legais</li>
                <li>Em caso de fusão, aquisição ou venda de ativos</li>
              </ul>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">4. Cookies e Tecnologias Similares</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide mb-4">Utilizamos cookies para melhorar sua experiência:</p>
              <ul className="list-disc list-inside space-y-2 mb-6 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Manter você conectado</li>
                <li>Lembrar suas preferências</li>
                <li>Analisar o tráfego do site</li>
                <li>Personalizar conteúdo</li>
              </ul>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">Você pode desativar cookies nas configurações do seu navegador, mas isso pode afetar algumas funcionalidades do site.</p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">5. Segurança dos Dados</h2>
              <ul className="list-disc list-inside space-y-2 mb-6 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Criptografia SSL/TLS em todas as transmissões</li>
                <li>Armazenamento seguro em servidores protegidos</li>
                <li>Acesso restrito aos dados pessoais</li>
                <li>Monitoramento contínuo de segurança</li>
                <li>Backups regulares</li>
              </ul>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">6. Seus Direitos (LGPD)</h2>
              <ul className="list-disc list-inside space-y-2 mb-6 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li><strong className="text-luxury-charcoal">Confirmação e acesso:</strong> Saber se processamos seus dados e acessá-los</li>
                <li><strong className="text-luxury-charcoal">Correção:</strong> Corrigir dados incompletos, inexatos ou desatualizados</li>
                <li><strong className="text-luxury-charcoal">Anonimização ou exclusão:</strong> Solicitar a remoção de dados desnecessários</li>
                <li><strong className="text-luxury-charcoal">Portabilidade:</strong> Receber seus dados em formato estruturado</li>
                <li><strong className="text-luxury-charcoal">Revogação de consentimento:</strong> Retirar autorização a qualquer momento</li>
                <li><strong className="text-luxury-charcoal">Oposição:</strong> Opor-se ao tratamento de dados</li>
              </ul>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">Para exercer seus direitos, entre em contato: <strong className="text-luxury-charcoal">contato@cartafuturo.org</strong></p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">7. Retenção de Dados</h2>
              <ul className="list-disc list-inside space-y-2 font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide ml-4">
                <li>Cumprir o propósito da carta (até a data de entrega escolhida)</li>
                <li>Cumprir obrigações legais e fiscais (5 anos após a transação)</li>
                <li>Resolver disputas e fazer cumprir nossos acordos</li>
              </ul>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">8. Menores de Idade</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">Nosso serviço é destinado a pessoas maiores de 18 anos. Não coletamos intencionalmente dados de menores de idade sem o consentimento dos pais ou responsáveis.</p>
            </div>

            <div className="mb-12 pb-12 border-b border-gold-500/10">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">9. Alterações nesta Política</h2>
              <p className="font-['Montserrat'] text-sm text-luxury-charcoal/70 leading-relaxed tracking-wide">Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre mudanças significativas através do email cadastrado ou por meio de aviso em nosso site.</p>
            </div>

            <div className="mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-luxury-charcoal mb-6">10. Contato</h2>
              <div className="bg-gradient-to-br from-gold-50 to-luxury-champagne/30 rounded-lg p-8 border border-gold-500/20">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-mail-line text-gold-600 text-lg mt-1"></i>
                    <div>
                      <p className="font-['Montserrat'] text-xs text-luxury-charcoal/50 uppercase tracking-wider mb-1">Email</p>
                      <p className="font-['Montserrat'] text-sm text-luxury-charcoal font-medium">contato@cartafuturo.org</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-shield-user-line text-gold-600 text-lg mt-1"></i>
                    <div>
                      <p className="font-['Montserrat'] text-xs text-luxury-charcoal/50 uppercase tracking-wider mb-1">Encarregado de Dados (DPO)</p>
                      <p className="font-['Montserrat'] text-sm text-luxury-charcoal font-medium">dpo@cartafuturo.org</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Privacidade;
